import { useState, useCallback } from 'react';
import Api from '../api';

interface UseLanguageCheckReturn {
  checkIfLanguagesNeeded: () => Promise<boolean>;
  needsLanguages: boolean | null;
  isChecking: boolean;
  error: string | null;
}

/**
 * Custom hook to check if a user needs to add languages to their profile
 * and manage the language checking state
 */
export const useLanguageCheck = (): UseLanguageCheckReturn => {
  const [needsLanguages, setNeedsLanguages] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkIfLanguagesNeeded = useCallback(async (): Promise<boolean> => {
    try {
      setIsChecking(true);
      setError(null);
      
      const { needsLanguages: apiResult } = await Api.getNeedsLanguages();
      setNeedsLanguages(apiResult);
      
      return apiResult;
    } catch (err) {
      console.error('Language check failed:', err);
      setError('Failed to check language status');
      setNeedsLanguages(false); // Default to false to not block user flow
      return false; // Don't block offer flow if language check fails
    } finally {
      setIsChecking(false);
    }
  }, []);

  return {
    checkIfLanguagesNeeded,
    needsLanguages,
    isChecking,
    error
  };
}; 